/**
 * Error handler utility to sanitize database error messages
 * Prevents information disclosure by mapping internal errors to user-friendly messages
 */

// Map of known error patterns to user-friendly messages
const ERROR_MAP: Record<string, string> = {
  // Auth errors
  'Invalid login credentials': 'Email ou senha incorretos',
  'already registered': 'Este email já está cadastrado',
  'Email not confirmed': 'Por favor, confirme seu email antes de fazer login',
  'invalid_grant': 'Sessão expirada. Faça login novamente',
  
  // RLS/Permission errors
  'row-level security': 'Você não tem permissão para realizar esta ação',
  'new row violates row-level security policy': 'Você não tem permissão para realizar esta ação',
  'violates foreign key constraint': 'Operação não permitida: dados relacionados existentes',
  
  // Unique constraint violations
  'duplicate key': 'Este registro já existe',
  'unique constraint': 'Este registro já existe',
  
  // Not found
  'no rows returned': 'Registro não encontrado',
  'PGRST116': 'Registro não encontrado',
  
  // Network/Connection
  'Failed to fetch': 'Erro de conexão. Verifique sua internet',
  'NetworkError': 'Erro de rede. Verifique sua conexão',
  'timeout': 'A operação demorou muito. Tente novamente',
  
  // Storage errors
  'Payload too large': 'O arquivo é muito grande',
  'Invalid file type': 'Tipo de arquivo não permitido',
  'storage/object-not-found': 'Arquivo não encontrado',
};

// Patterns to detect sensitive information that should never be shown
const SENSITIVE_PATTERNS = [
  /column\s+"?\w+"?\s+(?:of|does not exist)/i,
  /relation\s+"?\w+"?\s+does not exist/i,
  /constraint\s+"?\w+"/i,
  /violates check constraint/i,
  /function\s+\w+\.\w+/i,
  /schema\s+"?\w+"?/i,
  /table\s+"?\w+"?/i,
  /trigger\s+"?\w+"?/i,
  /index\s+"?\w+"?/i,
  /syntax error at/i,
  /permission denied for/i,
  /at character \d+/i,
  /DETAIL:/i,
  /HINT:/i,
  /CONTEXT:/i,
];

/**
 * Sanitizes an error message for user display
 * @param error - The error object or message
 * @returns A user-friendly error message
 */
export function sanitizeError(error: unknown): string {
  const defaultMessage = 'Ocorreu um erro. Tente novamente.';
  
  if (!error) return defaultMessage;
  
  // Extract message from error object
  let message: string;
  if (typeof error === 'string') {
    message = error;
  } else if (error instanceof Error) {
    message = error.message;
  } else if (typeof error === 'object' && 'message' in error) {
    message = String((error as { message: unknown }).message);
  } else {
    return defaultMessage;
  }
  
  // Check for known error patterns
  for (const [pattern, userMessage] of Object.entries(ERROR_MAP)) {
    if (message.toLowerCase().includes(pattern.toLowerCase())) {
      return userMessage;
    }
  }
  
  // Check if message contains sensitive information
  for (const pattern of SENSITIVE_PATTERNS) {
    if (pattern.test(message)) {
      console.error('Sensitive error hidden from user:', message);
      return defaultMessage;
    }
  }
  
  // If the message is short and doesn't match sensitive patterns, it might be safe
  // But we still log the full error for debugging
  if (message.length > 100) {
    console.error('Long error message hidden from user:', message);
    return defaultMessage;
  }
  
  // Return the message if it seems safe
  return message;
}

/**
 * Creates a safe error handler for async operations
 * @param operation - Description of the operation for logging
 */
export function createErrorHandler(operation: string) {
  return (error: unknown): string => {
    console.error(`Error in ${operation}:`, error);
    return sanitizeError(error);
  };
}
